// The wire format for a shared game row.
//
// This used to be scattered inline through sb.js, where `state_compressed` and
// `changediffs_compressed` were written with plain JSON.stringify despite their
// names — a median game state is ~291kB of raw JSON against ~40kB deflated, sent
// on every update in both directions. Keeping the rules in one place makes them
// testable (see wire.test.mjs) and means a reader and a writer cannot drift apart.

import { deflateSync, inflateSync, strToU8, strFromU8 } from 'fflate';
import { fromByteArray, toByteArray } from 'base64-js';

// Legacy rows hold raw JSON, which always starts with '{'. Anything else is
// deflate+base64. Both are readable so games live across the deploy keep working.
export function looksLikeRawJSON(payload){
    return typeof payload == 'string' && payload.trimStart().startsWith('{');
}

export function encodePayload(obj){
    return fromByteArray(deflateSync(strToU8(JSON.stringify(obj))));
}

export function decodePayload(payload){
    if(!payload) return false;
    if(looksLikeRawJSON(payload)) return JSON.parse(payload);
    return JSON.parse(strFromU8(inflateSync(toByteArray(payload))));
}

// Monotonic per game, and carried inside the state object itself so it needs no
// new column on the collection.
export function nextVersion(stateVersion, lastKnownVersion){
    let a = typeof stateVersion == 'number' ? stateVersion : 0;
    let b = typeof lastKnownVersion == 'number' ? lastKnownVersion : 0;
    return Math.max(a, b) + 1;
}

// Did a full state arriving from the server conflict with the one we hold?
//
//   'stale'    — it is older than ours, so the writer had not seen our latest move.
//   'diverged' — same version, different content: two clients both derived vN from
//                vN-1, which is what "same username on two devices" looks like.
//
// Same version and same content is just a re-delivery (a reconnect, or an explicit
// pull of a row we already applied) and is not a conflict.
//
// This detects; it cannot prevent. Stopping the second write from landing at all
// needs the server to reject it, which is a different piece of work.
export function conflictKind(incomingVersion, heldVersion, contentDiffers){
    if(typeof incomingVersion != 'number' || typeof heldVersion != 'number') return false;
    if(incomingVersion < heldVersion) return 'stale';
    if(incomingVersion === heldVersion && contentDiffers) return 'diverged';
    return false;
}

// A JSON-Patch is only valid against the exact state it was computed from. This
// is what lets a diff update travel *without* the full state attached: if the
// versions disagree we have drifted and must pull the whole thing instead.
// Legacy patches carry no `base`, so they keep the old unconditional behaviour.
export function patchAppliesTo(incomingPatch, heldVersion){
    if(!incomingPatch || !incomingPatch.patch) return false;
    if(typeof incomingPatch.base != 'number') return true;
    if(typeof heldVersion != 'number') return false;
    return heldVersion === incomingPatch.base;
}

// The row holds the last full state written plus, at most, one diff — and every
// diff is made against that full state, not against the diff before it. So the
// row alone always yields the latest state: its state blob, with its diff applied
// when the diff was made against that blob. A diff made against some other state
// (left behind by a write that replaced the blob but not the diff column) is
// ignored, and the blob is the latest thing the row can vouch for.
//
// Before this, diffs chained off one another while the blob stayed put, so
// pulling the row after drifting handed back a state several versions old — which
// the conflict check then reported as someone overwriting your move.
//
// `applyPatch` is fast-json-patch's, passed in so this file needs no bundler.
export function resolveRow(rowState, rowPatch, applyPatch){
    if(!rowState) return false;
    if(!rowPatch || !rowPatch.patch) return rowState;
    // Legacy diffs carry no base; they were always made against the row's blob.
    if(typeof rowPatch.base == 'number' && rowPatch.base !== rowState.stateVersion) return rowState;
    let next = JSON.parse(JSON.stringify(rowState));
    try{
        applyPatch(next, rowPatch.patch);
    }catch(e){
        return rowState;
    }
    if(typeof rowPatch.version == 'number') next.stateVersion = rowPatch.version;
    return next;
}
